import { websiteConfig } from '@/config/website';
import type { Price, PricePlan } from '@/payment/types';

function normalizePriceId(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export function getPriceIdCandidates(price: Price): string[] {
  const ids = [normalizePriceId(price.priceId)].filter(Boolean);
  return Array.from(new Set(ids));
}

export function isPriceIdMatch(price: Price, priceId: string): boolean {
  const target = normalizePriceId(priceId);
  if (!target) return false;
  return getPriceIdCandidates(price).includes(target);
}

/**
 * Get all price plans (without translations, like name/description/features)
 * NOTICE: This function can be used in server or client components.
 * @returns Array of price plans
 */
export const getAllPricePlans = (): PricePlan[] => {
  return Object.values(websiteConfig.price.plans);
};

/**
 * Get plan by plan ID
 * @param planId Plan ID
 * @returns Plan or undefined if not found
 */
export const findPlanByPlanId = (planId: string): PricePlan | undefined => {
  return getAllPricePlans().find((plan) => plan.id === planId);
};

/**
 * Find plan by price ID
 * @param priceId Price ID (PayPal plan ID or internal SKU)
 * @returns Plan or undefined if not found
 */
export const findPlanByPriceId = (priceId: string): PricePlan | undefined => {
  const plans = getAllPricePlans();
  for (const plan of plans) {
    const matchingPrice = plan.prices.find((price) =>
      isPriceIdMatch(price, priceId)
    );
    if (matchingPrice) {
      return plan;
    }
  }
  return undefined;
};

/**
 * Find price in a plan by ID
 * @param planId Plan ID
 * @param priceId Price ID (PayPal plan ID or internal SKU)
 * @returns Price or undefined if not found
 */
export const findPriceInPlan = (
  planId: string,
  priceId: string
): Price | undefined => {
  const plan = findPlanByPlanId(planId);
  if (!plan) {
    console.error(`findPriceInPlan, Plan with ID ${planId} not found`);
    return undefined;
  }
  return plan.prices.find((price) => isPriceIdMatch(price, priceId));
};

/**
 * Find price by price ID
 * @param priceId Price ID (PayPal plan ID or internal SKU)
 * @returns Price or undefined if not found
 */
export const findPriceByPriceId = (priceId: string): Price | undefined => {
  const plan = findPlanByPriceId(priceId);
  return plan?.prices.find((price) => isPriceIdMatch(price, priceId));
};
